import { useTranslation } from 'react-i18next';
import { Skeleton, SkeletonGroup } from '../ui';
import { SalonResultCardSkeleton } from './salon-card';
import { SiteShell, type SiteShellVariant } from './site-shell';

/**
 * Page loading skeletons (M7). Each page shows its own skeleton while its
 * data loads, and the router shows the same one while the page's code chunk
 * loads (`app/app.tsx`), so the swap from chunk → data → content never moves
 * the layout. Kept outside the lazy pages so the main bundle can render them.
 */

const container = 'mx-auto w-full max-w-[1360px] px-gutter-m md:px-8 lg:px-gutter';

/** Board 03 while the salon loads. */
export function SalonPageSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t('salon.profile.loading')}
      className="mx-auto w-full max-w-[1360px] md:px-8 md:pt-5 lg:px-gutter"
    >
      <Skeleton className="hidden h-4 w-48 md:block" />
      <Skeleton radius={20} className="h-[200px] w-full rounded-none md:mt-3 md:h-[330px] md:rounded-20" />
      <div className="px-gutter-m pt-5 md:px-0 md:pt-6">
        <Skeleton className="h-9 w-2/3 max-w-[420px]" />
        <Skeleton className="mt-3 h-4 w-1/2 max-w-[520px]" />
        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1fr)_300px] lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} radius={18} className="h-[72px] w-full" />
            ))}
          </div>
          <Skeleton radius={20} className="hidden h-[340px] w-full md:block" />
        </div>
      </div>
    </div>
  );
}

/** Booking flow (boards 04–06) before the salon is known: minimal bar + step + summary. */
export function FlowSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-sl-bg">
      <div className="h-nav-m border-b border-sl-line md:h-[76px]" />
      <SkeletonGroup label={t('booking.flow.loading')}>
        <div className="mx-auto grid w-full max-w-[1360px] grid-cols-1 gap-8 px-gutter-m pt-8 md:grid-cols-[minmax(0,1fr)_300px] md:px-8 lg:grid-cols-[minmax(0,780px)_380px] lg:gap-12 lg:px-gutter lg:pt-12">
          <div className="space-y-3">
            <Skeleton className="h-10 w-3/4 max-w-[520px]" />
            <Skeleton className="h-4 w-1/2 max-w-[420px]" />
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} radius={16} className="h-[80px] w-full" />
            ))}
          </div>
          <Skeleton radius={20} className="hidden h-[340px] w-full md:block" />
        </div>
      </SkeletonGroup>
    </div>
  );
}

/** My bookings (board 08) body. */
export function AccountPageSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonGroup label={t('account.loading')}>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,780px)_380px] lg:gap-12">
        <div className="space-y-4">
          <Skeleton className="h-11 w-64" />
          <Skeleton radius={20} className="h-[260px] w-full" />
        </div>
        <Skeleton radius={20} className="h-[170px] w-full" />
      </div>
    </SkeletonGroup>
  );
}

/** Reschedule body. */
export function RescheduleSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonGroup label={t('account.loading')}>
      <Skeleton className="h-11 w-72" />
      <Skeleton radius={20} className="mt-6 h-[320px] w-full max-w-[780px]" />
    </SkeletonGroup>
  );
}

/** Account routes (`/me/*`) inside the marketing shell. */
export function AccountShellSkeleton({ reschedule = false }: { reschedule?: boolean }) {
  return (
    <SiteShell>
      <div className={`${container} py-12`}>{reschedule ? <RescheduleSkeleton /> : <AccountPageSkeleton />}</div>
    </SiteShell>
  );
}

/** Board 02 shape: search band, then result cards (filters rail from lg). */
export function SearchShellSkeleton() {
  const { t } = useTranslation();
  return (
    <SiteShell>
      <div className="border-b border-sl-line bg-sl-surface">
        <div className="mx-auto w-full max-w-[1360px] px-gutter-m py-3 md:px-8 md:py-4 lg:px-gutter-results">
          <Skeleton radius="pill" className="h-12 w-full max-w-[772px]" />
          <div className="mt-3 flex gap-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} radius="pill" className="h-9 w-24 shrink-0" />
            ))}
          </div>
        </div>
      </div>
      <div className="mx-auto w-full max-w-[1360px] px-gutter-m py-6 md:px-8 lg:px-gutter-results lg:py-7">
        <div className="grid grid-cols-1 gap-[26px] lg:grid-cols-[236px_minmax(0,1fr)] xl:grid-cols-[236px_minmax(0,1fr)_330px]">
          <Skeleton radius={18} className="hidden h-[420px] w-full lg:block" />
          <SkeletonGroup label={t('search.searching')} className="flex min-w-0 flex-col gap-4">
            <Skeleton radius="pill" className="h-7 w-1/2" />
            {[0, 1, 2, 3].map((i) => (
              <SalonResultCardSkeleton key={i} />
            ))}
          </SkeletonGroup>
          <Skeleton radius={18} className="hidden h-[522px] w-full xl:block" />
        </div>
      </div>
    </SiteShell>
  );
}

/** Auth (boards 09–12), confirmation, 404: a centred block in the focused shell. */
export function CenteredShellSkeleton({ variant = 'minimal' }: { variant?: SiteShellVariant }) {
  return (
    <SiteShell variant={variant}>
      <div className="flex min-h-[calc(100svh-58px)] w-full items-center justify-center px-gutter-m py-12 md:min-h-[calc(100svh-77px)] md:py-16">
        <SkeletonGroup className="flex w-full max-w-[420px] flex-col items-center">
          <Skeleton radius="pill" className="h-[74px] w-[74px]" />
          <Skeleton radius="pill" className="mt-6 h-9 w-64 max-w-full" />
          <Skeleton radius="pill" className="mt-4 h-4 w-3/4" />
        </SkeletonGroup>
      </div>
    </SiteShell>
  );
}

/** The salon page while its chunk loads (same as its data-loading state). */
export function SalonShellSkeleton() {
  return (
    <SiteShell>
      <SalonPageSkeleton />
    </SiteShell>
  );
}
